import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import Breadcrumb from "../../../App/Components/Navigation/Breadcrumbs/Breadcrumbs";
import { useSelector } from "react-redux";
import { selectCareersClientValue } from "../../../App/State/Slices/SelectedCareersClientSlice";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  useTheme,
} from "@mui/material";
import DropdownComponent from "../../../App/Components/Dropdown/DropdownComponent";
import YearMonthDayPicker from "../../../App/Components/Datepickers/YearMonthDayPicker";
import { getGeneralReport, getUserList } from "../ReportsService";
import LineGraph from "../../../App/Components/Graphs/LineGraph";
import {
  basicConvertDate,
  convertDateMonth,
} from "../../../App/Helpers/DateConverters";
import SortableTable from "../../../App/Components/Table/SortableTable";
import ReportsTableCell from "./ReportsTableCell";
import { useReportDateState } from "../State/ReportDateState";

function GeneralReport({ adTypes, optIn }) {
  const useStyles = makeStyles((theme) => ({
    root: {
      [theme.breakpoints.down("md")]: {
        width: "100%",
        paddingBottom: "80px",
      },
      height: "100%",
      display: "flex",
      flexDirection: "column",
      height: "100%",
    },
    cardContainer: {
      [theme.breakpoints.down("md")]: {
        width: "100%",
        overflowY: "scroll",
      },
      display: "flex",
      flexDirection: "column",
      marginBottom: "20px",
      width: "95%",
    },
    graph: {
      width: "95%",
      height: "30vh",
      [theme.breakpoints.down("md")]: {
        height: "300px",
      },
    },

    sensitiveButton: {
      marginTop: "20px",
      height: "50%",
      width: "90%",
      [theme.breakpoints.down("md")]: {
        width: "100%",
      },
    },
  }));

  const classes = useStyles();
  const theme = useTheme();

  const selectCareersClient = useSelector(selectCareersClientValue);
  const dateState = useReportDateState();
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aggregateData, setAggregateData] = useState(false);

  const [report, setReport] = useState({ dates: [], dateVal: [], users: [] });

  const graphData = {
    labels: report.dates,
    datasets: [
      {
        data: report.dateVal,
        fill: true,
        lineTension: 0.4,
        backgroundColor: theme.palette.primary.graph,
        borderColor: theme.palette.primary.main,
      },
    ],
  };

  const columns = [
    { label: "Name", propName: "fullName" },
    { label: "Email Address", propName: "email" },
    { label: "Created On", propName: "dateCreated" },
    { label: "Updated On", propName: "dateUpdated" },
  ];

  const setData = (response) => {
    response.dateVal = response.dates.map((a) => a.item2);
    response.dates = response.dates.map((a) => convertDateMonth(a.item1));
    response.users.forEach((row) => {
      if (row.firstName == null || row.lastName == null) {
        row.fullName = "Name Not Provided";
      } else {
        row.fullName = row.firstName + " " + row.lastName;
      }
      row.dateCreated = basicConvertDate(row.dateCreated);
      row.dateUpdated = basicConvertDate(row.dateUpdated);
    });
    setReport(response);
  };

  useEffect(() => {
    setLoading(true);
    getGeneralReport(
      selectCareersClient.id,
      dateState.getters.startDate.toDateString(),
      dateState.getters.endDate.toDateString(),
      null,
      null,
      null,
      optIn,
      aggregateData
    ).then((response) => {
      setData(response);
      setLoading(false);
    });
  }, [
    selectCareersClient,
    dateState.getters.startDate,
    dateState.getters.endDate,
    optIn,
  ]);

  const searchData = () => {
    let industryId =
      selectedIndustry?.id === undefined ? null : selectedIndustry.id;
    let familyId = selectedFamily?.id === undefined ? null : selectedFamily.id;
    let categoryId =
      selectedCategory?.id === undefined ? null : selectedCategory.id;

    setLoading(true);
    getGeneralReport(
      selectCareersClient.id,
      dateState.getters.startDate.toDateString(),
      dateState.getters.endDate.toDateString(),
      industryId,
      familyId,
      categoryId,
      optIn,
      aggregateData
    ).then((response) => {
      setData(response);
      setLoading(false);
    });
  };

  return (
    <div className={classes.root}>
      <Grid className={classes.cardContainer}>
        <Card className={classes.card}>
          <CardContent>
            <Grid container spacing={1} columns={14}>
              <Grid item xs={12} sm={2}>
                <YearMonthDayPicker
                  value={dateState.getters.startDate}
                  setValue={dateState.setters.setStartDate}
                  label="Start Date"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <YearMonthDayPicker
                  value={dateState.getters.endDate}
                  setValue={dateState.setters.setEndDate}
                  label="End Date"
                />
              </Grid>
              {adTypes.industries?.length > 0 ? (
                <Grid item xs>
                  <DropdownComponent
                    currValue={selectedIndustry}
                    items={adTypes.industries}
                    property="name"
                    title="Industry"
                    handleChange={(value) => setSelectedIndustry(value)}
                    emptyRow
                  />
                </Grid>
              ) : null}
              {adTypes.families?.length > 0 ? (
                <Grid item xs>
                  <DropdownComponent
                    currValue={selectedFamily}
                    items={adTypes.families}
                    property="name"
                    title="Family"
                    handleChange={(value) => setSelectedFamily(value)}
                    emptyRow
                  />
                </Grid>
              ) : null}
              {adTypes.categories?.length > 0 ? (
                <Grid item xs>
                  <DropdownComponent
                    currValue={selectedCategory}
                    items={adTypes.categories}
                    property="name"
                    title="Category"
                    handleChange={(value) => setSelectedCategory(value)}
                    emptyRow
                  />
                </Grid>
              ) : null}
              <Grid item xs>
                <FormControlLabel
                  style={{ marginTop: "10px" }}
                  control={
                    <Checkbox
                      color="primary"
                      checked={aggregateData}
                      onChange={() => setAggregateData(!aggregateData)}
                    />
                  }
                  label="Aggregate"
                />
              </Grid>
              <Grid item xs>
                <Button
                  style={{ marginTop: "20px", height: "50%", width: "90%" }}
                  className={classes.sensitiveButton}
                  type="submit"
                  variant="contained"
                  color="primary"
                  onClick={() => searchData()}
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <div className={classes.graph}>
        <LineGraph
          data={graphData}
          title={optIn ? "Marketing Signups" : "Job Alert Signups"}
          loading={loading}
        />
      </div>
      {report.users?.length > 0 && optIn ? (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={1} container>
            <Button
              className={classes.sensitiveButton}
              type="submit"
              variant="contained"
              color="primary"
              onClick={() => getUserList(selectCareersClient.id, report.users)}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      ) : null}
      {optIn ? (
        <SortableTable
          columns={columns}
          tableData={report.users}
          rowComponent={ReportsTableCell}
          loading={loading}
          hideSearch
        />
      ) : null}
    </div>
  );
}

export default GeneralReport;
